import { useEffect, useRef, useState } from 'react';
import { DEFAULT_STREAK_RULES, STREAK_KINDS, type StreakView } from '@london-life/contracts';
import { useCatalogue } from '../lib/content';
import { useDismiss } from '../lib/use-dismiss';

// a chat row's 🔥 n
export function StreakBadge({ streak }: { streak: StreakView | null | undefined }) {
  if (!streak?.count) return null;
  return (
    <span className="streak-badge" aria-label={`${streak.count}-day streak`}>
      🔥 {streak.count}
    </span>
  );
}

// the time left today, kept fresh once a minute
function useLeft(endsAt: string) {
  const [left, setLeft] = useState('');
  useEffect(() => {
    const tell = () => {
      const minutes = Math.max(0, Math.round((new Date(endsAt).getTime() - Date.now()) / 60_000));
      setLeft(minutes >= 60 ? `${Math.floor(minutes / 60)}h ${minutes % 60}m` : `${minutes}m`);
    };
    tell();
    const timer = window.setInterval(tell, 60_000);
    return () => window.clearInterval(timer);
  }, [endsAt]);
  return left;
}

// the chat header's 🔥 n, as in Lagos Life: tap it to see whose turn it is today, how long is left, and what
// counts (a pop-up that closes on a tap outside or Escape)
export function StreakButton({ streak, name }: { streak: StreakView; name: string }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useDismiss(open, () => setOpen(false), box);
  const rules = useCatalogue()?.streaks ?? DEFAULT_STREAK_RULES;
  const left = useLeft(streak.endsAt);
  const both = streak.mine && streak.theirs;
  const status = both
    ? 'You both did your part today 🎉'
    : streak.mine
      ? `You did your part. Waiting on ${name} before midnight`
      : streak.theirs
        ? `${name} did their part today. Your turn`
        : streak.count
          ? 'Do something together before midnight to keep it'
          : 'Do something together to start one';
  return (
    <div className="streak" ref={box}>
      <button
        type="button"
        className={`streak-button${streak.count ? '' : ' none'}`}
        aria-expanded={open}
        aria-label={streak.count ? `${streak.count}-day streak with ${name}` : `Streak with ${name}`}
        onClick={() => setOpen(!open)}
      >
        🔥 {streak.count}
      </button>
      {open && (
        <div className="streak-pop" role="dialog" aria-label="Streak">
          <strong>{streak.count ? `🔥 ${streak.count}-day streak with ${name}` : `No streak with ${name} yet`}</strong>
          <p>{status}</p>
          {!both && <p className="streak-left">⏳ {left} left today</p>}
          {streak.holding && (
            <p className="streak-save">🛟 This week&rsquo;s save is holding it. Do something together today.</p>
          )}
          {streak.best > 1 && <p className="streak-best">Best: {streak.best} days</p>}
          <small>What counts, once a day each:</small>
          <ul>
            {rules.counts.map((kind) => (
              <li key={kind}>
                <span aria-hidden="true">{STREAK_KINDS[kind].icon}</span> {STREAK_KINDS[kind].label}
              </li>
            ))}
          </ul>
          <small>
            Plain messages, stickers and money don&rsquo;t count.
            {rules.forgivenPerWeek > 0 &&
              ` ${rules.forgivenPerWeek === 1 ? 'One missed day' : `${rules.forgivenPerWeek} missed days`} a week ${rules.forgivenPerWeek === 1 ? 'is' : 'are'} forgiven.`}
          </small>
        </div>
      )}
    </div>
  );
}
